import { Trash2, UserX, X } from 'lucide-react';
import React, { useState } from 'react';

/**
 * Professional Remove Reason Modal
 * Based on conversation-mockup.html design
 * Unified modal for both QueryResultsList and ResultsSection
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Modal visibility
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onConfirm - Confirm removal callback
 * @param {Object} props.candidate - Candidate being removed
 */
export function RemoveReasonModal({
  isOpen,
  onClose,
  onConfirm,
  candidate
}) {
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const reasons = [
    {
      value: 'not-relevant',
      label: 'Not relevant to the role',
      description: "Skills or experience don't match requirements"
    },
    {
      value: 'insufficient-exp',
      label: 'Insufficient experience',
      description: 'Years of experience below requirements'
    },
    {
      value: 'missing-skills',
      label: 'Missing required skills',
      description: 'Lacks critical technical or soft skills'
    },
    {
      value: 'location-mismatch',
      label: 'Location mismatch',
      description: 'Not in required geographic area'
    },
    {
      value: 'other',
      label: 'Other',
      description: 'Specify custom reason'
    }
  ];

  const handleConfirm = () => {
    if (!selectedReason) {
      alert('Please select a reason for removal');
      return;
    }

    const finalReason = selectedReason === 'other' ? customReason : selectedReason;

    if (selectedReason === 'other' && !customReason.trim()) {
      alert('Please specify a reason');
      return;
    }

    onConfirm(finalReason);
    handleClose();
  };

  const handleClose = () => {
    setSelectedReason('');
    setCustomReason('');
    onClose();
  };

  const getDisplayName = (candidate) => {
    if (!candidate) return 'Unknown';
    return candidate.contactName || candidate.name || 'Unknown Candidate';
  };

  const getInitials = (name) => {
    if (!name || name === 'Unknown' || name === 'Unknown Candidate') return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Modal Header */}
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Remove Candidate</h2>
                <p className="text-xs text-gray-500 mt-0.5">Why are you removing this candidate?</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="px-5 py-4">
          {/* Candidate Info Card */}
          {candidate && (
            <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {getInitials(getDisplayName(candidate))}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    {getDisplayName(candidate)}
                  </h4>
                  <p className="text-xs text-gray-600">{candidate.title || 'No title'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Removal Reasons */}
          <div className="space-y-2">
            {reasons.map((reason) => (
              <label
                key={reason.value}
                className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition ${
                  selectedReason === reason.value
                    ? 'border-red-300 bg-red-50'
                    : 'border-gray-200 hover:border-red-200 hover:bg-red-50/50'
                }`}
              >
                <input
                  type="radio"
                  name="removeReason"
                  value={reason.value}
                  checked={selectedReason === reason.value}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="w-4 h-4 text-red-600 focus:ring-red-500 mt-0.5"
                />
                <div className="ml-3 flex-1">
                  <span className="font-medium text-gray-900 text-sm block">{reason.label}</span>
                  <p className="text-xs text-gray-600 mt-0.5">{reason.description}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Custom Reason Textarea */}
          {selectedReason === 'other' && (
            <div className="mt-3">
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Please specify the reason for removal..."
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedReason}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-4 h-4" />
            Remove Candidate
          </button>
        </div>
      </div>
    </div>
  );
}

export default RemoveReasonModal;

